import React, { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Advice, Icon, Link, Modal, useTheme } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';

import { PRODUCTS } from 'constants/Entities';
import { getProductFolders } from 'services/ProductsService';
import { toggleMatchingNodes } from 'utils/treeNavigation';
import { getLiteral } from 'utils/getLiteral';
import TreeHoi from 'components/TreeHoi';

import './styles.scss';

const propTypes = {
    fieldId: PropTypes.string,
    hidden: PropTypes.bool,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    shouldRenderField: PropTypes.func,
    value: PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
};

const SelectFamilyModal = ({ fieldId, hidden, onChange, readOnly, shouldRenderField, value }) => {
    const theme = useTheme();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [folderTree, setFolderTree] = useState(null);
    const [selected, setSelected] = useState({
        name: value?.label || '',
        id: value?.value || '',
    });

    // Fetch folders when modal opens
    const fetchFolders = useCallback(() => {
        function parseTreeFolder(childs) {
            return childs.map((child) => ({
                id: child.Id,
                name: child.FamilyName,
                parentId: child.IdParent,
                children: child.ChildFamily?.length ? parseTreeFolder(child.ChildFamily) : [],
            }));
        }

        getProductFolders({ idParent: -1 })
            .then((data) => {
                let families = [];
                if (data?.data) families = parseTreeFolder(data.data);
                setFolderTree((currentFamilies) => {
                    let newFamilies = {
                        id: '-1',
                        name: getLiteral('title_products'),
                        toggled: true,
                        children: families,
                    };

                    if (currentFamilies && families) {
                        toggleMatchingNodes(currentFamilies, newFamilies);
                    }

                    return newFamilies;
                });
            })
            .catch((e) => {
                console.error(e);
            });
    }, []);

    const openModal = useCallback(() => {
        if (readOnly) return;
        setSelected({
            name: value?.label || '',
            id: value?.value || '',
        });
        fetchFolders();
        setIsModalOpen(true);
    }, [fetchFolders, readOnly, value]);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const onSave = useCallback(() => {
        onChange?.({
            label: selected.name,
            value: selected.id,
        });
        setIsModalOpen(false);
    }, [onChange, selected]);

    const onClickItem = useCallback((folder) => {
        setSelected(folder);
    }, []);

    // Handle tree node toggle (expand/collapse)
    const onToggle = useCallback((node) => {
        const updateToggled = (tree, nodeId) => {
            if (!tree) return tree;
            if (String(tree.id) === String(nodeId)) {
                return { ...tree, toggled: !tree.toggled };
            }
            if (tree.children?.length > 0) {
                return {
                    ...tree,
                    children: tree.children.map((child) => updateToggled(child, nodeId)),
                };
            }
            return tree;
        };

        setFolderTree((prev) => updateToggled(prev, node.id));
    }, []);

    const setTreeNodeProps = useCallback((node, selectedId) => {
        if (!node) return node;
        const newNode = { ...node, active: String(node.id) === String(selectedId) };
        if (newNode.children?.length > 0) {
            newNode.children = newNode.children
                .map((child) => setTreeNodeProps(child, selectedId))
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
        }
        return newNode;
    }, []);

    const nodes = useMemo(() => {
        if (!folderTree) return null;
        return setTreeNodeProps(folderTree, selected?.id);
    }, [folderTree, selected?.id, setTreeNodeProps]);

    if (hidden) return null;
    if (shouldRenderField && !shouldRenderField(fieldId)) return null;

    const rootClass = classnames('fm-navigator-tree', {
        'fm-navigator-tree--empty': !nodes?.children?.length,
    });

    return (
        <div className="fm-field-select-family">
            <Link
                className="fm-field-select-family__display"
                variation="primary"
                isDisabled={readOnly}
                onClick={openModal}
            >
                {value?.label || getLiteral('title_products')}
                {!readOnly && <Icon name="edit" color={theme.colors.semantic.positive500} />}
            </Link>
            <Modal
                cancelText={getLiteral('action_cancel')}
                className="fm-family-tree-modal"
                classes={{
                    content: 'fm-family-tree-modal__content',
                    container: 'fm-family-tree-modal__container',
                }}
                confirmText={getLiteral('action_save')}
                isOpen={isModalOpen}
                onCancel={closeModal}
                onConfirm={onSave}
                onRequestClose={closeModal}
                size="large"
                title={getLiteral('title_product_families')}
            >
                <div className="fm-navigator fm-navigator__families">
                    <Advice className="fm-family-tree-modal__advice" showIcon type="info">
                        {getLiteral('label_products_select_family')}
                    </Advice>
                    <div className="fm-navigator-tree__container">
                        {nodes && (
                            <div className={rootClass}>
                                <TreeHoi
                                    canSelectParents
                                    entity={PRODUCTS}
                                    nodes={nodes}
                                    onSelect={onClickItem}
                                    onToggle={onToggle}
                                    withIcon
                                />
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

SelectFamilyModal.displayName = 'SelectFamilyModal';
SelectFamilyModal.propTypes = propTypes;

export default memo(SelectFamilyModal);
